import { useCallback, useMemo, useState } from 'react'

import { useForm } from 'react-hook-form'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'

import { storePendingToast } from 'Utils/pendingToast'

const DEFAULT_VALUES = {
  companyName: '',
  companyCode: '',
  vatin: '',
  country: '',
  state: '',
  zipCode: '',
  city: '',
  streetAddress: '',
}

const BASE_PATH = '/profile/subscription/company-details'

// Checkout guards answer with { redirectTo, message } at HTTP 200 instead of new payment intents.
function followRedirect(data) {
  if (!data?.redirectTo) return false

  storePendingToast({ type: data.flashType === 'notice' ? 'success' : 'error', message: data.message })
  window.location.href = data.redirectTo
  return true
}

export default function useCompanyForm({ onApplied, onReverted, savedCompanyData, isUpdateMode }) {
  const [isApplied, setIsApplied] = useState(false)
  const { postData, isLoading: isApplying } = useApiPost()
  const { deleteData } = useApiDelete()

  const apiPath = isUpdateMode ? `${BASE_PATH}?context=update` : BASE_PATH

  const initialValues = useMemo(() => {
    if (!savedCompanyData) return DEFAULT_VALUES

    return {
      companyName: savedCompanyData.companyName || '',
      companyCode: savedCompanyData.companyCode || '',
      vatin: savedCompanyData.vatin || '',
      country: savedCompanyData.country || '',
      state: savedCompanyData.state || '',
      zipCode: savedCompanyData.zipCode || '',
      city: savedCompanyData.city || '',
      streetAddress: savedCompanyData.streetAddress || '',
    }
  }, [savedCompanyData])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useForm({ defaultValues: initialValues, mode: 'onSubmit' })

  const watchCountry = watch('country')

  const onSubmit = useCallback((formData) => {
    postData({
      path: apiPath,
      data: formData,
      onSuccess: (response) => {
        if (followRedirect(response.data)) return

        setIsApplied(true)
        onApplied({ formData, ...response.data })
      },
      onError: (error) => {
        const fieldErrors = error.response?.data?.errors || {}

        if (Object.keys(fieldErrors).length > 0) {
          Object.entries(fieldErrors).forEach(([field, message]) => {
            setError(field, { type: 'server', message })
          })
        } else {
          App.events.publish(
            'app:notification:flash:error',
            'An error occurred while applying company details. Please try again.'
          )
        }
      },
    })
  }, [postData, onApplied, setError, apiPath])

  const handleApply = handleSubmit(onSubmit)

  const handleRevert = useCallback(() => {
    deleteData({
      path: apiPath,
      onSuccess: (response) => {
        if (followRedirect(response.data)) return

        setIsApplied(false)
        onReverted(response.data || {})
      },
      onError: () => {
        App.events.publish(
          'app:notification:flash:error',
          'An error occurred while disabling company details. Please try again.'
        )
      },
    })
  }, [deleteData, onReverted, apiPath])

  const handleFieldChange = useCallback(() => {
    if (isApplied) {
      setIsApplied(false)
      onReverted()
    }
  }, [isApplied, onReverted])

  const resetForm = useCallback(() => {
    setIsApplied(false)
    reset(initialValues)
  }, [reset, initialValues])

  return {
    control,
    errors,
    watchCountry,
    isApplied,
    isApplying,
    handleApply,
    handleFieldChange,
    handleRevert,
    resetForm,
    getValues,
    setValue,
  }
}
